import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { RouteGuard } from "@/components/auth/RouteGuard";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { isSuperAdmin } from "@/lib/permissions";
import { useConfigModules } from "@/features/configuration/api";
import {
  ApprovalRulesTab, FormFieldsTab, MasterDataTab, NotificationRulesTab,
  SlaRulesTab, StatusesTab, WorkflowsTab,
} from "@/features/configuration/tabs";
import { CodeGenerationTab } from "@/features/configuration/CodeGenerationTab";
import { AuditLogTab } from "@/features/configuration/AuditLogTab";
import { Alert, AlertDescription } from "@/components/ui/alert";

export const Route = createFileRoute("/_authenticated/configuration")({
  component: () => (
    <RouteGuard module="configuration"><ConfigurationCentre /></RouteGuard>
  ),
});

function ConfigurationCentre() {
  const { data: me } = useCurrentUser();
  const readOnly = !isSuperAdmin(me?.roles ?? []);
  const { data: modules, isLoading: modulesLoading, error: modulesError } = useConfigModules();
  const [moduleKey, setModuleKey] = useState<string>("");
  const [tab, setTab] = useState<string>("master-data");

  // Default selected module: work_orders if present, else first active.
  useEffect(() => {
    if (!moduleKey && modules && modules.length > 0) {
      const preferred = modules.find((m) => m.module_key === "work_orders");
      setModuleKey((preferred ?? modules[0]).module_key);
    }
  }, [moduleKey, modules]);

  return (
    <div className="space-y-4 p-4 md:p-6">
      <div>
        <h1 className="text-2xl font-semibold">Configuration Centre</h1>
        <p className="text-sm text-muted-foreground">Configure master data, statuses, workflows, approvals, SLAs, form fields, and notifications.</p>
      </div>

      {readOnly && (
        <Alert>
          <AlertDescription>You have read-only access. Only Super Admin can modify configuration.</AlertDescription>
        </Alert>
      )}

      <div className="flex items-end gap-3">
        <div className="space-y-1">
          <Label className="text-xs">Module</Label>
          <Select value={moduleKey} onValueChange={setModuleKey} disabled={modulesLoading || !modules?.length}>
            <SelectTrigger className="w-64">
              <SelectValue placeholder={modulesLoading ? "Loading modules..." : "Select module"} />
            </SelectTrigger>
            <SelectContent>
              {(modules ?? []).map((m) => (
                <SelectItem key={m.module_key} value={m.module_key}>{m.module_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {modulesError && (
            <p className="text-xs text-destructive">Unable to load configuration modules.</p>
          )}
          {!modulesLoading && !modulesError && modules && modules.length === 0 && (
            <p className="text-xs text-muted-foreground">No configuration modules found. Please check seed data.</p>
          )}
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex flex-wrap">
          <TabsTrigger value="master-data">Master Data</TabsTrigger>
          <TabsTrigger value="code-generation">Code Generation</TabsTrigger>
          <TabsTrigger value="statuses">Statuses</TabsTrigger>
          <TabsTrigger value="workflows">Workflows</TabsTrigger>
          <TabsTrigger value="approvals">Approval Rules</TabsTrigger>
          <TabsTrigger value="sla">SLA Rules</TabsTrigger>
          <TabsTrigger value="notifications">Notifications</TabsTrigger>
          <TabsTrigger value="audit">Audit Log</TabsTrigger>
        </TabsList>
        <TabsContent value="master-data"><MasterDataTab moduleKey={moduleKey} readOnly={readOnly} /></TabsContent>
        <TabsContent value="code-generation"><CodeGenerationTab readOnly={readOnly} /></TabsContent>
        <TabsContent value="statuses"><StatusesTab moduleKey={moduleKey} readOnly={readOnly} /></TabsContent>
        <TabsContent value="workflows"><WorkflowsTab moduleKey={moduleKey} readOnly={readOnly} /></TabsContent>
        <TabsContent value="approvals"><ApprovalRulesTab moduleKey={moduleKey} readOnly={readOnly} /></TabsContent>
        <TabsContent value="sla"><SlaRulesTab moduleKey={moduleKey} readOnly={readOnly} /></TabsContent>
        <TabsContent value="notifications"><NotificationRulesTab moduleKey={moduleKey} readOnly={readOnly} /></TabsContent>
        <TabsContent value="audit"><AuditLogTab /></TabsContent>
      </Tabs>
    </div>
  );
}
